import { deserialize, fetchWrapper } from '../../fetch';
import type {
  ChatHistoryResponse,
  PostMessageResponse,
  ProposalActionResponse,
  ScopeItemRequest,
  Thread,
} from './interface';

const BASE = '/app/v1/dossiers';

export class ThreadGoneError extends Error {
  constructor(public threadId: string) {
    super(`thread ${threadId} not found`);
    this.name = 'ThreadGoneError';
  }
}

async function fetchThread<T>(
  href: string,
  init: RequestInit,
  threadId: string,
  timeoutLimit?: number
): Promise<T> {
  try {
    return await deserialize(await fetchWrapper(href, init, timeoutLimit));
  } catch (err) {
    // Duck-type on the HTTPError shape rather than `instanceof HTTPError`:
    // the latter can fail across jest module-sandbox boundaries where the
    // test imports a sibling copy of the class. `.response.status` is the
    // public contract.
    const status = (err as { response?: { status?: number } } | null)?.response
      ?.status;
    if (status === 404) {
      throw new ThreadGoneError(threadId);
    }
    throw err;
  }
}

export const listThreads = async (dossierId: string): Promise<Thread[]> => {
  const url = new URL(`${BASE}/${dossierId}/threads`, API_BASE_URL);
  const res: { threads: Thread[] } = await deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
  return res.threads;
};

export const createThread = async (
  dossierId: string,
  scope: ScopeItemRequest[] = []
): Promise<Thread> => {
  const url = new URL(`${BASE}/${dossierId}/threads`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ scope }),
    })
  );
};

export const getThread = async (
  dossierId: string,
  threadId: string
): Promise<Thread> => {
  const url = new URL(`${BASE}/${dossierId}/threads/${threadId}`, API_BASE_URL);
  return fetchThread<Thread>(
    url.href,
    { method: 'GET', credentials: 'include' },
    threadId
  );
};

export const updateScope = async (
  dossierId: string,
  threadId: string,
  scope: ScopeItemRequest[]
): Promise<Thread> => {
  const url = new URL(`${BASE}/${dossierId}/threads/${threadId}`, API_BASE_URL);
  return fetchThread<Thread>(
    url.href,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ scope }),
    },
    threadId
  );
};

export const closeThread = async (
  dossierId: string,
  threadId: string
): Promise<Thread> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/close`,
    API_BASE_URL
  );
  return fetchThread<Thread>(
    url.href,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
    },
    threadId
  );
};

export const reopenThread = async (
  dossierId: string,
  threadId: string
): Promise<Thread> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/reopen`,
    API_BASE_URL
  );
  return fetchThread<Thread>(
    url.href,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
    },
    threadId
  );
};

export const deleteThread = async (
  dossierId: string,
  threadId: string
): Promise<void> => {
  const url = new URL(`${BASE}/${dossierId}/threads/${threadId}`, API_BASE_URL);
  await fetchThread<void>(
    url.href,
    { method: 'DELETE', credentials: 'include' },
    threadId
  );
};

export const fetchMessages = async (
  dossierId: string,
  threadId: string
): Promise<ChatHistoryResponse> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/messages`,
    API_BASE_URL
  );
  return fetchThread<ChatHistoryResponse>(
    url.href,
    { method: 'GET', credentials: 'include' },
    threadId
  );
};

export const postMessage = async (
  dossierId: string,
  threadId: string,
  text: string
): Promise<PostMessageResponse> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/messages`,
    API_BASE_URL
  );
  // No timeout: the backend currently holds the connection open for the
  // entire agent reply (see `post_thread_message` in the backend) and only
  // returns IDs once `_stream_agent_reply` finishes. Until the BE returns
  // immediately and runs the reply as a background task, the FE has to
  // accept a long-lived POST. Matches the legacy `agentChatApiClient`
  // behavior for the same reason.
  return fetchThread<PostMessageResponse>(
    url.href,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ text }),
    },
    threadId,
    -1
  );
};

export const approveProposal = async (
  dossierId: string,
  threadId: string,
  proposalId: string
): Promise<ProposalActionResponse> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/proposals/${proposalId}/approve`,
    API_BASE_URL
  );
  return fetchThread<ProposalActionResponse>(
    url.href,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
    },
    threadId
  );
};

export const rejectProposal = async (
  dossierId: string,
  threadId: string,
  proposalId: string,
  reason?: string
): Promise<ProposalActionResponse> => {
  const url = new URL(
    `${BASE}/${dossierId}/threads/${threadId}/proposals/${proposalId}/reject`,
    API_BASE_URL
  );
  return fetchThread<ProposalActionResponse>(
    url.href,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
    threadId
  );
};
